// src/pages/messages-ai/MessagesPage.jsx
// Message history list for a team — lists all AI-generated messages ordered newest first.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, query, orderBy, onSnapshot, doc, deleteDoc,
  updateDoc, writeBatch, limit,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  PlusIcon, ClipboardDocumentIcon, CheckIcon,
  TrashIcon, ArrowPathIcon, PaperAirplaneIcon,
  MagnifyingGlassIcon, DevicePhoneMobileIcon,
  EnvelopeIcon, ExclamationTriangleIcon, BoltIcon,
  FunnelIcon, InboxArrowDownIcon, ChatBubbleLeftEllipsisIcon,
  SparklesIcon, CloudIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { MESSAGE_TYPES, TYPE_COLORS } from './messageTypes';
import TeamSubNav from '@/components/team/TeamSubNav';

/* ------------------------------------------------------------------ */
/* Share icons                                                          */
/* ------------------------------------------------------------------ */

const WhatsAppIcon = () => (
  <svg className="h-3.5 w-3.5 fill-white flex-shrink-0" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
  </svg>
);

const SmsIcon = () => (
  <svg className="h-3.5 w-3.5 fill-white flex-shrink-0" viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
    <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function getTypeMeta(id) {
  return MESSAGE_TYPES.find((t) => t.id === id) || {
    id,
    icon: '💬',
    label: id,
    color: 'blue',
  };
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(ts);
}

/* ------------------------------------------------------------------ */
/* Copy button with feedback                                            */
/* ------------------------------------------------------------------ */

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={handle}
      title="Copy message"
      className={[
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
        copied
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700',
        className,
      ].join(' ')}
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Status badge                                                         */
/* ------------------------------------------------------------------ */

function StatusBadge({ status, broadcastStatus }) {
  if (status === 'sent') {
    // A green "Sent" over a partial/failed broadcast tells the coach
    // everyone got the message when they didn't — badge must match reality.
    if (broadcastStatus === 'partial' || broadcastStatus === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
          <ExclamationTriangleIcon className="h-3 w-3" />
          {broadcastStatus === 'partial' ? 'Partly sent' : 'Send failed'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
        <CheckIcon className="h-3 w-3" />
        Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
      Draft
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Urgent badge + filter summary                                        */
/* ------------------------------------------------------------------ */

function UrgentBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400"
      title="Sent as urgent — bypasses quiet hours for SMS-opted-in parents"
    >
      <BoltIcon className="h-3 w-3" />
      Urgent
    </span>
  );
}

// Human-readable chips for the filters that were applied when this
// broadcast went out. Season is stored as an id on the message doc, so
// we show a neutral "Filtered by season" chip — the audit doc has the
// full id if anyone needs to reconstruct it.
function describeFilters(filters) {
  if (!filters) return [];
  const out = [];
  if (filters.paymentStatus === 'paid')   out.push('Paid only');
  if (filters.paymentStatus === 'unpaid') out.push('Unpaid only');
  if (filters.language === 'en')          out.push('English only');
  if (filters.language === 'es')          out.push('Español only');
  if (filters.seasonId)                   out.push('Season filter');
  return out;
}

// Phase 5: badge for drafts that were seeded by an event (weather cancel,
// schedule change). Helps the coach spot "oh that's why this is here" vs
// drafts they typed themselves, and hints at the source so they know the
// context before opening.
function SourceBadge({ message }) {
  if (!message?.autoDrafted && !message?.source?.type) return null;
  const type = message.source?.type;

  const isWeather  = type === 'weather_cancel';
  const isSchedule = type === 'schedule_change';

  const Icon = isWeather ? CloudIcon
              : isSchedule ? CalendarDaysIcon
              : SparklesIcon;

  const label = isWeather
    ? 'From weather alert'
    : isSchedule
      ? (message.source?.changeType === 'cancellation' ? 'From game cancel' : 'From schedule change')
      : 'Auto-drafted';

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:text-violet-400"
      title="Scrimly pre-filled this draft for you. Review, personalize, and send when ready."
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function FilterSummary({ filters, className = '' }) {
  const parts = describeFilters(filters);
  if (!parts.length) return null;
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 dark:text-indigo-400',
        className,
      ].join(' ')}
      title={`Audience filter: ${parts.join(' · ')}`}
    >
      <FunnelIcon className="h-3 w-3" />
      {parts.length === 1 ? parts[0] : `${parts.length} filters`}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Send receipt — "what actually happened when I sent this"             */
/* ------------------------------------------------------------------ */

// Renders once a message has been broadcast. Mirrors the audit doc
// under teams/{}/broadcasts so the coach doesn't need to click through.
// Hidden for draft/legacy-sent messages that predate the broadcast
// stamp (broadcastId missing → nothing to show).
function SendReceipt({ message }) {
  if (message.status !== 'sent' || !message.broadcastId) return null;

  const {
    smsSent = 0, smsFailed = 0, smsHeld = 0,
    emailSent = 0, emailFailed = 0,
    unreachableCount = 0,
    rosterTotal, filteredCount,
    filters,
    broadcastStatus = 'sent',
  } = message;

  const totalSent   = smsSent + emailSent;
  const totalFailed = smsFailed + emailFailed;

  // Tone the receipt card to match the outcome so partial failures
  // don't hide behind the success green.
  const toneClasses = broadcastStatus === 'failed'
    ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20'
    : broadcastStatus === 'partial'
      ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20'
      : 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/20';

  const headlineTone = broadcastStatus === 'failed'
    ? 'text-red-800 dark:text-red-300'
    : broadcastStatus === 'partial'
      ? 'text-amber-800 dark:text-amber-300'
      : 'text-emerald-800 dark:text-emerald-300';

  // Held (quiet-hours) texts are listed apart from failures — "failed"
  // reads as breakage, but a held text is the compliance window working.
  const headlineParts = [`Sent to ${totalSent} ${totalSent === 1 ? 'parent' : 'parents'}`];
  if (totalFailed > 0) headlineParts.push(`${totalFailed} failed`);
  if (smsHeld > 0) headlineParts.push(`${smsHeld} ${smsHeld === 1 ? 'text' : 'texts'} held (quiet hours)`);
  const headline = broadcastStatus === 'failed'
    ? 'Send failed'
    : headlineParts.join(' · ');

  return (
    <div className={`rounded-xl border px-4 py-3 ${toneClasses}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className={`text-sm font-semibold ${headlineTone}`}>{headline}</p>
        {typeof filteredCount === 'number' && typeof rosterTotal === 'number' && filteredCount !== rosterTotal && (
          <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
            {filteredCount} of {rosterTotal} on roster
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-700 dark:text-zinc-300">
        {smsSent > 0 && (
          <span className="inline-flex items-center gap-1">
            <DevicePhoneMobileIcon className="h-3.5 w-3.5" />
            {smsSent} SMS
          </span>
        )}
        {emailSent > 0 && (
          <span className="inline-flex items-center gap-1">
            <EnvelopeIcon className="h-3.5 w-3.5" />
            {emailSent} email
          </span>
        )}
        {totalFailed > 0 && (
          <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {totalFailed} failed
            {smsFailed > 0 && emailFailed > 0
              ? ` (${smsFailed} SMS · ${emailFailed} email)`
              : ''}
          </span>
        )}
        {smsHeld > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
            <DevicePhoneMobileIcon className="h-3.5 w-3.5" />
            {smsHeld} held
          </span>
        )}
        {unreachableCount > 0 && (
          <span className="text-zinc-500 dark:text-zinc-400">
            {unreachableCount} unreachable
          </span>
        )}
      </div>

      {smsHeld > 0 && (
        <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
          Texts aren&apos;t sent between 10 PM and 7 AM. Resend after 7 AM to reach
          {smsHeld === 1 ? ' this parent' : ' these parents'} by SMS.
        </p>
      )}

      {describeFilters(filters).length > 0 && (
        <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
          Audience: {describeFilters(filters).join(' · ')}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Inbound SMS replies (Phase 4)                                        */
/* ------------------------------------------------------------------ */

// Format a phone as (415) 555-1234 for display. Keep the E.164 version
// on the `tel:`/`sms:` links so dialer apps open correctly.
function formatPhone(e164) {
  if (!e164) return '';
  const digits = String(e164).replace(/\D/g, '');
  const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
  if (last10.length !== 10) return e164;
  return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
}

// Languages we explicitly support in the toggle UI. Anything else falls
// through to "render original text, no meta strip, no toggle" — safer
// than rendering a meta strip in a language we haven't translated keys
// into. Keep in sync with INBOUND_TRANSLATION_TARGETS in
// functions/modules/inboundReplies.js.
const TOGGLE_SUPPORTED_LANGS = new Set(['en', 'es']);

// Resolve which text to render in a ReplyItem and whether a translation
// meta strip + toggle should appear.
//
// Returns `{ isTranslation, translatedText, sourceLang }`:
//   - `isTranslation: true` when there's a usable translation in the
//     viewer's language AND the source language differs from the
//     viewer's. Caller renders `translatedText` by default and shows
//     the meta strip with a "Show original" toggle.
//   - `isTranslation: false` when the source already matches the
//     viewer's language (no work needed), the translation was skipped,
//     the translation failed, or the doc is from before this feature
//     shipped (no `translations` field at all). Caller renders the
//     original `body` with no meta strip.
//
// Falls back to "no meta strip" for any uncertainty — we never want to
// render a broken toggle or claim a translation we don't actually have.
function getDisplayBody(reply, viewerLang) {
  const lang = String(viewerLang || 'en').slice(0, 2);
  const sourceLang = reply?.originalLanguage;
  const translatedText = reply?.translations?.[lang];
  if (
    sourceLang &&
    TOGGLE_SUPPORTED_LANGS.has(sourceLang) &&
    TOGGLE_SUPPORTED_LANGS.has(lang) &&
    sourceLang !== lang &&
    typeof translatedText === 'string' &&
    translatedText.trim().length > 0
  ) {
    return { isTranslation: true, translatedText, sourceLang };
  }
  return { isTranslation: false, translatedText: null, sourceLang: null };
}

// Shared: mark a single reply as read. Used by both the inbox list and
// the threaded-under-a-message view.
async function markReplyRead(teamId, replyId) {
  try {
    await updateDoc(doc(db, 'teams', teamId, 'inboundSms', replyId), {
      read: true,
    });
  } catch (err) {
    console.error('[MessagesPage] mark reply read failed', err);
    toast.error('Could not mark as read.');
  }
}

// One reply row — used both in the top-of-page Inbox and nested inside a
// MessageCard's reply thread. Keeping it reusable means the two places
// stay visually consistent without duplicated markup.
//
// When the inbound SMS has been auto-translated (originalLanguage +
// translations populated by functions/modules/inboundReplies.js), the
// coach sees the message in their own preferred language by default,
// with a "Show original" toggle that swaps in the parent's raw words.
// See features/inbound-sms-translation/ for the full spec.
function ReplyItem({ reply, teamId }) {
  const { t, i18n } = useTranslation();
  const viewerLang = String(i18n?.language || 'en').slice(0, 2);
  const phoneDisplay = formatPhone(reply.phone);
  const fromLine = reply.parentName || phoneDisplay || 'Unknown parent';
  const replyHref = reply.phone
    ? `sms:${reply.phone}${/iPhone|iPad|Mac/.test(navigator.userAgent) ? '&' : '?'}body=`
    : null;

  // Translation-aware body resolution. `getDisplayBody` returns
  // `isTranslation: false` for old docs (no translations field), same-
  // language replies, skipped/failed translations, and any uncertainty
  // — so the meta strip + toggle only appears when we actually have
  // something to swap.
  const display = getDisplayBody(reply, viewerLang);
  const [showOriginal, setShowOriginal] = useState(false);
  const bodyText = display.isTranslation && !showOriginal
    ? display.translatedText
    : reply.body;

  // Render the source-language name in the viewer's locale (e.g. an EN
  // coach seeing a Spanish reply gets "Spanish"; an ES coach seeing an
  // English reply gets "inglés"). Falls back to the language code if
  // for some reason the key is missing — safer than throwing.
  const sourceLangLabel = display.sourceLang === 'es'
    ? t('inboundReply.languageSpanish', 'Spanish')
    : display.sourceLang === 'en'
      ? t('inboundReply.languageEnglish', 'English')
      : display.sourceLang || '';

  return (
    <div
      className={[
        'rounded-xl border px-4 py-3 transition-colors',
        reply.read
          ? 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
          : 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-900/20',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {!reply.read && (
            <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" aria-label="Unread" />
          )}
          <span className="text-sm font-semibold text-zinc-900 dark:text-white truncate">
            {fromLine}
          </span>
          {reply.parentName && phoneDisplay && (
            <span className="text-xs text-zinc-400 truncate">{phoneDisplay}</span>
          )}
        </div>
        <span className="text-[11px] text-zinc-400 flex-shrink-0">
          {formatRelative(reply.receivedAt)}
        </span>
      </div>
      <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
        {bodyText}
      </p>
      {display.isTranslation && (
        <div className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2 flex-wrap">
          <span>
            {showOriginal
              ? t('inboundReply.originalLanguage', {
                  defaultValue: 'Original ({{language}})',
                  language: sourceLangLabel,
                })
              : t('inboundReply.translatedFrom', {
                  defaultValue: 'Translated from {{language}}',
                  language: sourceLangLabel,
                })}
          </span>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="text-blue-600 dark:text-blue-400 hover:underline font-medium py-1 -my-1 min-h-[24px]"
          >
            {showOriginal
              ? t('inboundReply.showTranslation', 'Show translation')
              : t('inboundReply.showOriginal', 'Show original')}
          </button>
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {replyHref && (
          <a
            href={replyHref}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-400 transition-colors"
          >
            <DevicePhoneMobileIcon className="h-3.5 w-3.5" />
            Reply via SMS
          </a>
        )}
        {!reply.read && (
          <button
            type="button"
            onClick={() => markReplyRead(teamId, reply.id)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
          >
            <CheckIcon className="h-3.5 w-3.5" />
            Mark read
          </button>
        )}
      </div>
    </div>
  );
}

// Top-of-page Inbox — shows unread replies with a "mark all read" action.
// Hidden when the inbox is empty so it doesn't steal attention on quiet
// days. Read replies are still discoverable from the per-message thread.
function ReplyInbox({ replies, teamId }) {
  const unread = replies.filter((r) => !r.read);
  if (unread.length === 0) return null;

  const markAllRead = async () => {
    try {
      const batch = writeBatch(db);
      unread.forEach((r) => {
        batch.update(doc(db, 'teams', teamId, 'inboundSms', r.id), { read: true });
      });
      await batch.commit();
      toast.success(`Marked ${unread.length} ${unread.length === 1 ? 'reply' : 'replies'} as read.`);
    } catch (err) {
      console.error('[MessagesPage] mark-all-read failed', err);
      toast.error('Could not mark as read.');
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-[#b5f03d]/40 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#b5f03d]/20 bg-[#b5f03d]/10">
        <div className="flex items-center gap-2">
          <InboxArrowDownIcon className="h-5 w-5 text-[#5a8a1f]" />
          <span className="text-sm font-bold text-[#0d1f14]">
            {unread.length} new {unread.length === 1 ? 'reply' : 'replies'}
          </span>
        </div>
        <button
          type="button"
          onClick={markAllRead}
          className="text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
        >
          Mark all read
        </button>
      </div>
      <div className="px-5 py-4 space-y-3">
        {unread.slice(0, 5).map((reply) => (
          <ReplyItem key={reply.id} reply={reply} teamId={teamId} />
        ))}
        {unread.length > 5 && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center pt-1">
            +{unread.length - 5} more — open a sent message below to see all replies in context.
          </p>
        )}
      </div>
    </div>
  );
}

// Threaded replies under a single MessageCard. Rendered in the expanded
// view so the coach can see the conversation without leaving the message.
function ReplyThread({ replies, teamId }) {
  if (!replies || replies.length === 0) return null;
  const unreadCount = replies.filter((r) => !r.read).length;

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200">
          <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
          Replies ({replies.length})
        </div>
        {unreadCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:text-blue-300">
            {unreadCount} new
          </span>
        )}
      </div>
      <div className="px-4 py-3 space-y-3 bg-white dark:bg-zinc-900">
        {replies.map((reply) => (
          <ReplyItem key={reply.id} reply={reply} teamId={teamId} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Message card                                                         */
/* ------------------------------------------------------------------ */

function MessageCard({ message, teamId, onDelete, replies = [] }) {
  const [expanded, setExpanded] = useState(false);
  const meta = getTypeMeta(message.messageType);
  const colors = TYPE_COLORS[meta.color] || TYPE_COLORS.blue;

  return (
    <div
      className={[
        'rounded-2xl border bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden transition-all',
        expanded ? 'border-[#b5f03d]/40' : 'border-zinc-200 hover:border-[#b5f03d]/30',
      ].join(' ')}
    >
      {/* Header row — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-[#f7faf5] transition-colors"
      >
        {/* Icon */}
        <span className={`flex-shrink-0 text-xl flex items-center justify-center w-10 h-10 rounded-xl ${colors.icon}`}>
          {meta.icon}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-zinc-900 dark:text-white">
              {meta.label}
            </span>
            <StatusBadge status={message.status} broadcastStatus={message.broadcastStatus} />
            {message.urgent && <UrgentBadge />}
            <SourceBadge message={message} />
            <FilterSummary filters={message.filters} />
            {replies.length > 0 && (
              <span
                className={[
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  replies.some((r) => !r.read)
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
                ].join(' ')}
                title={`${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
              >
                <ChatBubbleLeftEllipsisIcon className="h-3 w-3" />
                {replies.length}
              </span>
            )}
            <span className="text-xs text-zinc-400 ml-auto">
              {formatRelative(message.createdAt)}
            </span>
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate leading-relaxed">
            {message.bodyEn?.slice(0, 120)}{message.bodyEn?.length > 120 ? '…' : ''}
          </p>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-zinc-100 dark:border-zinc-800 px-5 pb-5 pt-4 space-y-4">

          {/* Auto-drafted hint — only for drafts seeded by an event.
              Once a draft has been sent we hide this to reduce noise. */}
          {message.autoDrafted && message.status === 'draft' && (
            <div className="rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50 dark:bg-violet-900/20 px-4 py-3 flex items-start gap-2">
              <SparklesIcon className="h-4 w-4 text-violet-600 dark:text-violet-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-violet-800 dark:text-violet-300 leading-relaxed">
                Scrimly pre-filled this draft after
                {' '}
                {message.source?.type === 'weather_cancel' && 'you marked the event as cancelled/postponed for weather'}
                {message.source?.type === 'schedule_change' && (message.source?.changeType === 'cancellation' ? 'a game cancellation' : 'a schedule change')}
                {!['weather_cancel', 'schedule_change'].includes(message.source?.type) && 'a team event'}
                . Review the text below, personalize anything you'd like, then tap a share/send action.
              </p>
            </div>
          )}

          {/* Send receipt — only for messages that were actually broadcast */}
          <SendReceipt message={message} />

          {/* Replies — only when this message has matched inbound SMS */}
          <ReplyThread replies={replies} teamId={teamId} />

          {/* English */}
          <div className="rounded-xl border border-blue-200 dark:border-blue-800 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-800 dark:text-blue-300">
                <span>🇺🇸</span> English
              </div>
              <CopyButton text={message.bodyEn} />
            </div>
            <p className="px-4 py-3 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
              {message.bodyEn}
            </p>
            <div className="px-4 pb-3 flex gap-2 flex-wrap">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(message.bodyEn)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1ebe57] transition-colors"
              >
                <WhatsAppIcon /> WhatsApp
              </a>
              <a
                href={`sms:?body=${encodeURIComponent(message.bodyEn)}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-400 transition-colors"
              >
                <SmsIcon /> SMS
              </a>
            </div>
          </div>

          {/* Spanish */}
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                <span>🇲🇽</span> Español
              </div>
              <CopyButton text={message.bodyEs} />
            </div>
            <p className="px-4 py-3 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
              {message.bodyEs}
            </p>
            <div className="px-4 pb-3 flex gap-2 flex-wrap">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(message.bodyEs)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1ebe57] transition-colors"
              >
                <WhatsAppIcon /> WhatsApp
              </a>
              <a
                href={`sms:?body=${encodeURIComponent(message.bodyEs)}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-400 transition-colors"
              >
                <SmsIcon /> SMS
              </a>
            </div>
          </div>

          {/* Copy both + delete */}
          <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
            <CopyButton
              text={`🇺🇸 English:\n${message.bodyEn}\n\n🇲🇽 Español:\n${message.bodyEs}`}
              className="text-xs"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(message.id); }}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                          */
/* ------------------------------------------------------------------ */

function EmptyState({ hasSearch, onCompose }) {
  if (hasSearch) {
    return (
      <div className="text-center py-16">
        <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-zinc-300 dark:text-zinc-700 mb-4" />
        <p className="text-zinc-500 dark:text-zinc-400">No messages match your search.</p>
      </div>
    );
  }
  return (
    <div className="text-center py-20">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-6">
        <PaperAirplaneIcon className="h-8 w-8 text-[#5a8a1f]" />
      </div>
      <h3
        className="text-xl font-bold uppercase text-[#0d1f14] mb-2"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        No messages yet
      </h3>
      <p className="text-sm text-zinc-500 max-w-sm mx-auto mb-6">
        Generate your first bilingual message in under a minute. Game recaps, reminders, payments — all ready in English and Spanish.
      </p>
      <button
        type="button"
        onClick={onCompose}
        className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase transition-all active:scale-[0.97]"
        style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
      >
        <PlusIcon className="h-4 w-4" />
        Create Your First Message
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Quick-start bar                                                      */
/* ------------------------------------------------------------------ */

const QUICK_TYPES = ['Reminder', 'Game Recap', 'Schedule Change', 'Payment Due'];

function hasLastDetails(typeId) {
  try {
    const raw = localStorage.getItem(`scrimly.lastDetails.${typeId}`);
    return !!raw && Object.keys(JSON.parse(raw) || {}).length > 0;
  } catch {
    return false;
  }
}

function QuickStartBar({ teamId, onNavigate }) {
  // Re-check localStorage on every render so badges appear after first use
  const [hasData, setHasData] = useState({});

  useEffect(() => {
    const map = {};
    QUICK_TYPES.forEach((id) => { map[id] = hasLastDetails(id); });
    setHasData(map);
  }, []);

  const types = useMemo(
    () => MESSAGE_TYPES.filter((t) => QUICK_TYPES.includes(t.id)),
    [],
  );

  return (
    <div className="mb-6">
      <p
        className="text-xs font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2.5"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        Quick Start
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {types.map((t) => {
          const colors = TYPE_COLORS[t.color] || TYPE_COLORS.blue;
          const remembered = hasData[t.id];
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onNavigate(`/teams/${teamId}/messages/new?type=${encodeURIComponent(t.id)}`)}
              className={[
                'relative flex flex-col items-center gap-1.5 rounded-2xl border px-3 py-3.5 text-center transition-all hover:shadow-md active:scale-[.97]',
                colors.bg, colors.border,
              ].join(' ')}
            >
              {/* "Last used" dot */}
              {remembered && (
                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#b5f03d]" title="Pre-filled from last use" />
              )}
              <span className="text-2xl">{t.icon}</span>
              <span className="text-xs font-semibold text-[#0d1f14] leading-tight">{t.label}</span>
              {remembered && (
                <span className="text-[10px] text-[#5a8a1f] font-bold">Pre-filled ✓</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Filter tabs                                                          */
/* ------------------------------------------------------------------ */

const FILTER_TABS = [
  { id: 'all',   label: 'All' },
  { id: 'sent',  label: 'Sent' },
  { id: 'draft', label: 'Drafts' },
];

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */

export default function MessagesPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  // Live listener for composer messages (drafts + sent records)
  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'teams', teamId, 'messages'),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('[MessagesPage] snapshot error', err);
      setLoading(false);
    });
    return unsub;
  }, [teamId]);

  // Phase 4: Live listener for inbound SMS replies. Capped at 50 docs
  // ordered newest-first so we don't read the entire history on every
  // page load — older replies are still in Firestore but out of view.
  const [replies, setReplies] = useState([]);
  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'teams', teamId, 'inboundSms'),
      orderBy('receivedAt', 'desc'),
      limit(50),
    );
    const unsub = onSnapshot(q, (snap) => {
      setReplies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      // Non-fatal — missing-collection errors are expected before the
      // first reply lands, and rule denials would just hide the Inbox.
      console.warn('[MessagesPage] replies snapshot error', err);
    });
    return unsub;
  }, [teamId]);

  // Map messageId → replies[] for the threaded view. One pass over
  // replies is O(N); map lookup per message card is O(1).
  const repliesByMessageId = useMemo(() => {
    const map = new Map();
    replies.forEach((r) => {
      if (!r.matchedMessageId) return;
      const arr = map.get(r.matchedMessageId) || [];
      arr.push(r);
      map.set(r.matchedMessageId, arr);
    });
    return map;
  }, [replies]);

  // Delete
  const handleDelete = async (msgId) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'teams', teamId, 'messages', msgId));
      toast.success('Message deleted.');
    } catch (err) {
      console.error(err);
      toast.error('Could not delete message.');
    }
  };

  // Filter + search
  const visible = messages.filter((m) => {
    if (filter !== 'all' && m.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        m.messageType?.toLowerCase().includes(q) ||
        m.bodyEn?.toLowerCase().includes(q) ||
        m.bodyEs?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sentCount  = messages.filter((m) => m.status === 'sent').length;
  const draftCount = messages.filter((m) => m.status === 'draft').length;

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={teamId} />

        {/* ---- Header ---- */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1
              className="text-2xl font-bold text-[#0d1f14] uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Messages
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Draft bilingual updates and send them straight to your roster —
              SMS for opted-in parents, email for everyone else.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/teams/${teamId}/messages/new`)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-sm flex-shrink-0 transition-all active:scale-[0.97]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' }}
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">New Message</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>

        {/* ---- Quick-start buttons ---- */}
        <QuickStartBar teamId={teamId} onNavigate={navigate} />

        {/* ---- Stats row ---- */}
        {messages.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Total',  value: messages.length, color: 'text-[#0d1f14]' },
              { label: 'Sent',   value: sentCount,        color: 'text-emerald-600' },
              { label: 'Drafts', value: draftCount,       color: 'text-amber-600' },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{s.value}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ---- Inbox (Phase 4) — surfaces unread parent replies ---- */}
        <ReplyInbox replies={replies} teamId={teamId} />

        {/* ---- Search + filter ---- */}
        {messages.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            {/* Search */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages…"
                className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-4 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d] transition-colors"
              />
            </div>

            {/* Status tabs */}
            <div className="flex rounded-xl border border-zinc-200 bg-white p-1 gap-1 self-start">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setFilter(tab.id)}
                  className={[
                    'px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap',
                    filter === tab.id
                      ? 'bg-[#b5f03d] text-[#0d1f14] shadow-sm'
                      : 'text-zinc-500 hover:text-[#0d1f14]',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---- Content ---- */}
        {loading ? (
          <div className="flex justify-center py-20">
            <ArrowPathIcon className="h-7 w-7 text-zinc-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState hasSearch={false} onCompose={() => navigate(`/teams/${teamId}/messages/new`)} />
        ) : visible.length === 0 ? (
          <EmptyState hasSearch={!!search || filter !== 'all'} onCompose={() => navigate(`/teams/${teamId}/messages/new`)} />
        ) : (
          <div className="space-y-3">
            {visible.map((msg) => (
              <MessageCard
                key={msg.id}
                message={msg}
                teamId={teamId}
                onDelete={handleDelete}
                replies={repliesByMessageId.get(msg.id) || []}
              />
            ))}
          </div>
        )}

        {/* ---- Quick-tip banner (first-time hint) ---- */}
        {messages.length > 0 && messages.length < 3 && (
          <div className="mt-8 rounded-2xl border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-5 py-4">
            <p className="text-sm font-semibold text-[#0d1f14] mb-1">💡 Tip</p>
            <p className="text-sm text-[#5a8a1f]">
              Each message is saved here so you can copy it again any time — or share it across multiple messaging apps.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
